export type ProcessAgent = {
  id: string;
  code: string;
  name: string;
  domain: string;
  mission: string;
  modelRoute: string;
  tools: string[];
  evidence: string;
  proactiveTriggers: string[];
};

export type ProcessStep = {
  id: string;
  title: string;
  description: string;
  agentId: string;
  duration: string;
  inputs: string[];
  outputs: string[];
  triggers: string[];
};

export type ProcessPhase = {
  id: string;
  title: string;
  subtitle: string;
  stream: "Analyse & Definition" | "Umsetzung" | "Nachhaltigkeit";
  objective: string;
  steps: ProcessStep[];
};

export const PROCESS_AGENTS: ProcessAgent[] = [
  {
    id: "intake",
    code: "IA",
    name: "Intake-Agent",
    domain: "Erstkontakt und Scope",
    mission: "Strukturiert Ziele, Schmerzpunkte, Erwartungen und Rollen, bevor Daten oder KPI-Werte aktiviert werden.",
    modelRoute: "lokal: DeepSeek V4 Flash 284B / Qwen-Fallback",
    tools: ["Onboarding-Profil", "Tenant-Policy", "CRM-Notizen"],
    evidence: "interview-profile.json + scope-brief.md",
    proactiveTriggers: ["neuer Tenant", "unvollständige Ziele", "unklare Owner"],
  },
  {
    id: "research",
    code: "RA",
    name: "Research-Agent",
    domain: "Markt und Branche",
    mission: "Recherchiert Branche, Wettbewerber, Regulatorik und externe Signale mit Lizenz- und Quellenkontrolle.",
    modelRoute: "public/anon: Crawl4AI + Nimble + OpenRouter policy-gated",
    tools: ["Crawl4AI", "Nimble", "SearXNG", "Benchmark-Registry"],
    evidence: "research-sources.json + citations.csv",
    proactiveTriggers: ["neue Website", "Benchmark veraltet", "Marktereignis"],
  },
  {
    id: "planning",
    code: "PA",
    name: "Planning-Agent",
    domain: "Projektplanung",
    mission: "Erstellt Zeitplan, Abhängigkeiten, Verantwortlichkeiten und Meilensteine für Time-to-First-Briefing.",
    modelRoute: "lokal: DeepSeek V4 Flash 284B",
    tools: ["Roadmap", "Capacity Model", "Approver Matrix"],
    evidence: "project-plan.yaml + milestone-log.json",
    proactiveTriggers: ["Scope geändert", "Meilenstein gefährdet", "Owner fehlt"],
  },
  {
    id: "document",
    code: "DA",
    name: "Document-Agent",
    domain: "Dokumente",
    mission: "Erzeugt Auftragsklärung, Methodik-Dossier, Ergebnisbericht und Audit-Anlagen.",
    modelRoute: "lokal für vertraulich, OpenRouter nur für anonymisierte Textvarianten",
    tools: ["Docling", "MinIO Evidence", "Documenso"],
    evidence: "document-manifest.json + doc-links.md",
    proactiveTriggers: ["Freigabe fällig", "Bericht unvollständig", "Signatur offen"],
  },
  {
    id: "analysis",
    code: "AN",
    name: "Analysis-Agent",
    domain: "IST-Analyse",
    mission: "Erkennt Muster, Lücken und Hypothesen aus Datenprofilen, Interviews und Prozessinformationen.",
    modelRoute: "lokal: DeepSeek V4 Flash 284B / Qwen-Fallback",
    tools: ["Trino", "OpenMetadata", "Profiling Fixtures"],
    evidence: "analysis-findings.json + assumptions.md",
    proactiveTriggers: ["neues Dataset", "Quality Score faellt", "Gap erkannt"],
  },
  {
    id: "data-extraction",
    code: "DE",
    name: "Data-Extraction-Agent",
    domain: "Datenextraktion",
    mission: "Extrahiert Daten aus Uploads, REST-Quellen, ERP/CRM-Syncs und Webquellen in Bronze/Silver.",
    modelRoute: "deterministisch + lokale Assistenz",
    tools: ["Airbyte", "Uploads", "Crawl4AI", "MinIO Bronze"],
    evidence: "extraction-run.json + source-manifest.csv",
    proactiveTriggers: ["Quelle hinzugefügt", "Sync überfällig", "Schema-Drift"],
  },
  {
    id: "data-quality",
    code: "DQ",
    name: "Data-Quality-Agent",
    domain: "Datenvertrauen",
    mission: "Prüft Vollständigkeit, Typen, Duplikate, Ausreisser und Contract-Verletzungen vor KPI-Freigabe.",
    modelRoute: "deterministisch, lokale Erklärungen",
    tools: ["dbt Tests", "Data Contracts", "OpenMetadata"],
    evidence: "quality-report.json + failing-rules.csv",
    proactiveTriggers: ["Quality < Schwelle", "Contract failed", "Null-Spike"],
  },
  {
    id: "data-integration",
    code: "DI",
    name: "Data-Integration-Agent",
    domain: "Integration",
    mission: "Mappt Quellen auf Silver/Gold-Marts, Tenant-Prefixes und Lineage-Knoten.",
    modelRoute: "lokal, keine externe Datenweitergabe",
    tools: ["Trino", "dbt", "OpenMetadata", "MinIO"],
    evidence: "lineage-map.json + integration-plan.yaml",
    proactiveTriggers: ["Mapping unvollständig", "neue Spalte", "Lineage fehlt"],
  },
  {
    id: "kpi-mining",
    code: "KM",
    name: "KPI-Mining-Agent",
    domain: "KPI Discovery",
    mission: "Findet KPI-Kandidaten aus Datenprofilen, Contracts, Branchenwissen und Zielen.",
    modelRoute: "heuristisch zuerst, lokal augmentiert, Cloud nur anonymisiert",
    tools: ["KPI Profiler", "Metric Contracts", "OpenMetadata"],
    evidence: "kpi-candidates.json + scoring-rationale.md",
    proactiveTriggers: ["neues Dataset", "zu wenige KPIs", "Ziel ohne Messgröße"],
  },
  {
    id: "process-mining",
    code: "PM",
    name: "Process-Mining-Agent",
    domain: "Prozesslogik",
    mission: "Verbindet Prozessschritte, Datenflüsse und Bottlenecks mit KPI-Wirkungen.",
    modelRoute: "lokal: DeepSeek V4 Flash 284B",
    tools: ["Event Logs", "Neo4j", "OpenMetadata"],
    evidence: "process-map.json + bottlenecks.csv",
    proactiveTriggers: ["Durchlaufzeit-Anomalie", "fehlender Prozess-Owner", "SLA verletzt"],
  },
  {
    id: "benchmark",
    code: "BA",
    name: "Benchmark-Agent",
    domain: "Marktvergleich",
    mission: "Ermittelt lizenzierte externe Vergleichswerte und trennt numerische Fakten von Marketingtext.",
    modelRoute: "Crawl4AI/Nimble, OpenRouter nur für öffentliche Zusammenfassung",
    tools: ["Crawl4AI", "Nimble", "Eurostat", "Bundesbank", "ClickHouse"],
    evidence: "benchmark-records.json + license-metadata.csv",
    proactiveTriggers: ["Benchmark TTL abgelaufen", "neuer Wettbewerber", "Marktwert fehlt"],
  },
  {
    id: "kpi-generator",
    code: "KG",
    name: "KPI-Generator-Agent",
    domain: "KPI Vorschlag",
    mission: "Erzeugt KPI-Sets für Branche, Rolle und Zielsystem mit Begründung und Review-Status.",
    modelRoute: "lokal, optional anonymisierte Naming-Hints",
    tools: ["KPI Library", "Metric Contracts", "Advisor Context"],
    evidence: "generated-kpi-set.json + review-notes.md",
    proactiveTriggers: ["neues Ziel", "Persona gewechselt", "KPI-Abdeckung niedrig"],
  },
  {
    id: "target-setting",
    code: "TS",
    name: "Target-Setting-Agent",
    domain: "Zielwerte",
    mission: "Berechnet Zielwert-Spannen aus Historie, Benchmarks, Risiko und Zielambition.",
    modelRoute: "lokal + statistisch",
    tools: ["Forecasts", "Benchmarks", "Monte-Carlo"],
    evidence: "target-ranges.json + assumptions.md",
    proactiveTriggers: ["neue Baseline", "Zielkonflikt", "Risiko zu hoch"],
  },
  {
    id: "kpi-definition",
    code: "KD",
    name: "KPI-Definition-Agent",
    domain: "Metric Governance",
    mission: "Formuliert versionierte KPI-Definitionen mit Owner, Formel, Datenklassifikation und Contract.",
    modelRoute: "lokal, Contract-Validator deterministisch",
    tools: ["YAML Contracts", "dbt", "Schema Validator"],
    evidence: "metric-contract.yaml + validation.json",
    proactiveTriggers: ["Definition fehlt", "Formel widersprüchlich", "Owner leer"],
  },
  {
    id: "prioritization",
    code: "PR",
    name: "Prioritization-Agent",
    domain: "Priorisierung",
    mission: "Sortiert Kandidaten, Handlungsfelder und Maßnahmen nach Impact, Confidence, Aufwand und Risiko.",
    modelRoute: "lokal: DeepSeek V4 Flash 284B",
    tools: ["Scoring Matrix", "Risk Register", "Impact Model"],
    evidence: "prioritization-scorecard.json",
    proactiveTriggers: ["zu viele Kandidaten", "Budget-Limit", "Risk-Score steigt"],
  },
  {
    id: "causal-analysis",
    code: "CA",
    name: "Causal-Analysis-Agent",
    domain: "Root Cause",
    mission: "Prüft Ursache-Wirkung-Hypothesen über Driver Trees, Korrelationen und Evidence-Snippets.",
    modelRoute: "lokal; externe Modelle gesperrt für vertrauliche Daten",
    tools: ["Neo4j", "Trino", "Anomaly/RCA"],
    evidence: "rca-hypotheses.json + graph-paths.csv",
    proactiveTriggers: ["Anomalie erkannt", "Driver unsicher", "Ursache unbewiesen"],
  },
  {
    id: "simulation",
    code: "SI",
    name: "Simulation-Agent",
    domain: "Szenario und Monte-Carlo",
    mission: "Simuliert P10/P50/P90-Szenarien, ROI, Cash-Risiko und Maßnahmeneffekte.",
    modelRoute: "lokal + deterministische Monte-Carlo Engine",
    tools: ["ROI Engine", "Forecast Worker", "MinIO Model Registry"],
    evidence: "simulation-result.json + seed.txt",
    proactiveTriggers: ["Business Case angefragt", "Forecast-Unsicherheit hoch", "Investitionsentscheidung"],
  },
  {
    id: "report",
    code: "RP",
    name: "Report-Agent",
    domain: "Bericht",
    mission: "Erstellt C-Level-Berichte mit Was/Warum/Was tun/Owner/Evidenz und Quellenbindung.",
    modelRoute: "lokal; externe Sprachpolitur nur anonymisiert",
    tools: ["Briefing Generator", "Docling", "Evidence Bundles"],
    evidence: "briefing.md + source-index.json",
    proactiveTriggers: ["Briefing fällig", "Anomalie kritisch", "Review abgeschlossen"],
  },
  {
    id: "presentation",
    code: "PS",
    name: "Presentation-Agent",
    domain: "Präsentation",
    mission: "Übersetzt Ergebnisse in Executive-Story, Workshop-Folien und Entscheidungsunterlagen.",
    modelRoute: "lokal für Inhalte, optional anonymisierte Stilvarianten",
    tools: ["Slide Builder", "Report-Agent", "Brand Tokens"],
    evidence: "presentation-outline.md + deck-manifest.json",
    proactiveTriggers: ["Workshop geplant", "Vorstandstermin", "Angebot fällig"],
  },
  {
    id: "recommendation",
    code: "RC",
    name: "Recommendation-Agent",
    domain: "Handlungsempfehlung",
    mission: "Formuliert konkrete Maßnahmen mit erwarteter Wirkung, Owner und HITL-Approval-Status.",
    modelRoute: "lokal; keine Auto-Ausführung",
    tools: ["Action Templates", "n8n", "Approvals"],
    evidence: "recommendations.json + approval-plan.md",
    proactiveTriggers: ["RCA abgeschlossen", "Zielabweichung", "Quick Win erkannt"],
  },
  {
    id: "communication",
    code: "CO",
    name: "Communication-Agent",
    domain: "Kommunikation",
    mission: "Plant Stakeholder-Kommunikation, Zielgruppenbotschaften und Freigabeschleifen.",
    modelRoute: "lokal, externe Modelle nur für öffentliche Texte",
    tools: ["Stakeholder Map", "Email/Teams/Slack Templates"],
    evidence: "communication-plan.md + audience-map.json",
    proactiveTriggers: ["Rollout startet", "Feedback negativ", "Entscheidung kommunizieren"],
  },
  {
    id: "content",
    code: "CT",
    name: "Content-Agent",
    domain: "Content",
    mission: "Erzeugt interne Hilfetexte, KPI-Erklärungen, Glossare und Enablement-Material.",
    modelRoute: "lokal oder anonymisiert je Content-Klasse",
    tools: ["KPI Catalog", "Knowledge Base", "Glossary"],
    evidence: "content-items.json + glossary-diff.md",
    proactiveTriggers: ["neuer KPI", "Training geplant", "Glossar-Lücke"],
  },
  {
    id: "feedback",
    code: "FB",
    name: "Feedback-Agent",
    domain: "Feedback Loop",
    mission: "Sammelt Feedback, bewertet Akzeptanz und führt Iterationen in KPI-Definitionen zurück.",
    modelRoute: "lokal",
    tools: ["Survey Results", "Review Notes", "Backlog"],
    evidence: "feedback-summary.json + decisions.md",
    proactiveTriggers: ["Workshop beendet", "KPI abgelehnt", "Nutzerfeedback neu"],
  },
  {
    id: "dashboard",
    code: "DB",
    name: "Dashboard-Agent",
    domain: "Cockpit",
    mission: "Stellt Cockpit-Widgets, KPI-Kacheln und Briefing-Ansichten erst nach Quality Gate bereit.",
    modelRoute: "deterministisch + lokale Assistenz",
    tools: ["Command Center", "Recharts", "Metric Store"],
    evidence: "dashboard-state.json + visibility-rules.json",
    proactiveTriggers: ["Quality Gate bestanden", "neuer KPI approved", "Widget veraltet"],
  },
  {
    id: "data",
    code: "DT",
    name: "Data-Agent",
    domain: "Data Plane",
    mission: "Verwaltet Bronze/Silver/Gold, Tenant-Prefixes, Readbacks und technische Datenpflege.",
    modelRoute: "deterministisch, keine externe Modellroute",
    tools: ["MinIO", "ClickHouse", "Trino", "Qdrant", "OpenSearch"],
    evidence: "data-plane-check.json + readback.csv",
    proactiveTriggers: ["Readback failed", "Index drift", "Storage policy verletzt"],
  },
  {
    id: "coaching",
    code: "CH",
    name: "Coaching-Agent",
    domain: "Enablement",
    mission: "Begleitet Owner, Controller und Führungskräfte bei Interpretation, Routine und KPI-Nutzung.",
    modelRoute: "lokal: DeepSeek V4 Flash 284B",
    tools: ["Learning Path", "Advisor", "Achievements"],
    evidence: "coaching-plan.md + adoption-metrics.json",
    proactiveTriggers: ["neuer Owner", "niedrige Adoption", "Briefing nicht gelesen"],
  },
];

export const PROCESS_PHASES: ProcessPhase[] = [
  {
    id: "1.1",
    title: "Pitch & Kennenlernen",
    subtitle: "Erstkontakt, Bedarfserfassung, Branchenanalyse und Vorgehens-Präsentation.",
    stream: "Analyse & Definition",
    objective: "Klares Mandat und belastbare Ausgangslage herstellen.",
    steps: [
      step("1.1.1", "Bedarfserfassung", "Strukturiertes Interview zur strategischen Ausgangslage, Schmerzpunkten und Zielen.", "intake", "30 min", ["Kontaktdaten", "Erstgespraech"], ["Kundenprofil", "Bedarfsanalyse"], ["neuer Lead", "unklare Zielrolle"]),
      step("1.1.2", "Branchenanalyse", "Recherche zu Branchendaten, Marktgröße, Wettbewerb und Regulierung.", "research", "automatisch", ["Branche", "Region", "Website"], ["Branchenprofil", "Marktdaten", "Wettbewerbsanalyse"], ["Website hinterlegt", "Benchmark fehlt"]),
      step("1.1.3", "Erwartungsmanagement", "Erfolgskriterien, Risiken und Vorgehen werden abgestimmt.", "intake", "15 min", ["Bedarfsanalyse"], ["Erwartungsprofil", "Erfolgskriterien"], ["Stakeholder uneinig"]),
      step("1.1.4", "Vorgehen-Präsentation", "Methodik, Prozess und Zeitrahmen werden als Executive-Story vorbereitet.", "presentation", "automatisch", ["Kundenprofil", "Branchenprofil"], ["Projektvorstellung", "Methodik-Übersicht"], ["Pitch geplant"]),
    ],
  },
  {
    id: "1.2",
    title: "Projektauftragsklärung",
    subtitle: "Scope, Ressourcen, Zeitplanung und Vertragsgestaltung.",
    stream: "Analyse & Definition",
    objective: "Scope und Governance so fixieren, dass Agenten auditierbar arbeiten können.",
    steps: [
      step("1.2.1", "Scope-Definition", "Grenzt Unternehmensbereiche, Datenklassen und Entscheidungsziele ab.", "planning", "45 min", ["Bedarfsanalyse", "Erfolgskriterien"], ["Scope-Statement"], ["Scope offen"]),
      step("1.2.2", "Ressourcenplanung", "Plant Owner, Approver, Datenverantwortliche und Workshop-Kapazität.", "planning", "30 min", ["Stakeholderliste"], ["RACI", "Approver-Matrix"], ["Owner fehlt"]),
      step("1.2.3", "Zeitplanung", "Erstellt Meilensteine bis zum ersten C-Level-Briefing.", "planning", "automatisch", ["Scope", "Kapazität"], ["Projektplan", "Milestone-Log"], ["SLA 4h gefährdet"]),
      step("1.2.4", "Vertragsgestaltung", "Bereitet AVV, externe Modellfreigaben und Compliance-Pack vor.", "document", "automatisch", ["Scope", "Datenklassifikation"], ["Vertragsanlage", "Policy-Entwurf"], ["externe Modelle angefragt"]),
    ],
  },
  {
    id: "1.3",
    title: "Angebotspruefung",
    subtitle: "Kalkulation, Angebot, Kundenprüfung und Nachverhandlung.",
    stream: "Analyse & Definition",
    objective: "Wirtschaftlichkeit und Leistungsumfang entscheidungsfaehig machen.",
    steps: [
      step("1.3.1", "Kostenkalkulation", "Berechnet Setup, Lizenz, Connector-Slots, Compute und Compliance-Pack.", "analysis", "30 min", ["Scope", "Pricing Tier"], ["Kalkulation"], ["Tier unklar"]),
      step("1.3.2", "Angebotserstellung", "Erzeugt ein Angebot mit Modulen, Annahmen und Erfolgskriterien.", "document", "automatisch", ["Kalkulation", "Scope"], ["Angebot", "Annahmenliste"], ["Angebot fällig"]),
      step("1.3.3", "Kundenprüfung", "Prüft Entscheidungsreife, Datenschutzanforderungen und Implementierungsrisiko.", "analysis", "30 min", ["Kundenprofil", "Policy"], ["Risk-Check", "Fit-Score"], ["Risk-Score hoch"]),
      step("1.3.4", "Nachverhandlung", "Bereitet Alternativen, Cuts und Mehrwertargumente vor.", "recommendation", "30 min", ["Feedback", "Preisrahmen"], ["Verhandlungsplan"], ["Preisfeedback"]),
    ],
  },
  {
    id: "1.4",
    title: "Projektkick-Off",
    subtitle: "Stakeholder, Kommunikation, Datenzugang und Kick-Off-Workshop.",
    stream: "Analyse & Definition",
    objective: "Mandantenkontext, Rollen und erste Datenwege aktivieren.",
    steps: [
      step("1.4.1", "Stakeholder-Mapping", "Ordnet Entscheider, Owner, DPO/CISO und Datenverantwortliche zu.", "planning", "30 min", ["Kontaktliste"], ["Stakeholder-Map"], ["neuer Stakeholder"]),
      step("1.4.2", "Kommunikationsplan", "Plant Cadence, Channels, Freigaben und Eskalationen.", "communication", "30 min", ["Stakeholder-Map"], ["Kommunikationsplan"], ["Projektstart"]),
      step("1.4.3", "Datenzugang klären", "Ermittelt Quellen, Berechtigungen, Tenant-Prefixes und Quality-Gates.", "data", "45 min", ["Systemliste", "Datenklassen"], ["Source-Backlog", "Access-Plan"], ["Quelle angefragt"]),
      step("1.4.4", "Kick-Off-Workshop", "Führt Methodik, Rollen, Datenvertrauen und nächste Schritte zusammen.", "presentation", "60 min", ["Projektplan", "Source-Backlog"], ["Workshop-Protokoll"], ["Kick-Off Termin"]),
    ],
  },
  {
    id: "1.5",
    title: "IST-Analyse",
    subtitle: "Datenextraktion, Qualität, externe Daten, IST-KPIs, Prozess- und Gap-Analyse.",
    stream: "Analyse & Definition",
    objective: "Faktenbasis ohne Scheinkennzahlen herstellen.",
    steps: [
      step("1.5.1", "Datenextraktion", "Lädt Uploads, ERP/CRM-Syncs und Webquellen in tenant-spezifische Bronze/Silver-Zonen.", "data-extraction", "automatisch", ["Quellen", "Credentials"], ["Bronze Dataset", "Source Manifest"], ["Quelle verbunden"]),
      step("1.5.2", "Qualitätsprüfung", "Validiert Vollständigkeit, Typen, Duplikate und Contract-Regeln.", "data-quality", "automatisch", ["Bronze Dataset"], ["Quality Score", "Fehlerliste"], ["Sync abgeschlossen"]),
      step("1.5.3", "Externe Datenintegration", "Ergaenzt öffentliche Markt-, Branchen- und Wettbewerbsdaten mit Lizenz-Metadaten.", "data-integration", "automatisch", ["Research Plan", "Crawl Seeds"], ["External Data Mart", "License Index"], ["Benchmark benötigt"]),
      step("1.5.4", "IST-KPI-Erfassung", "Inventarisiert bestehende Kennzahlen, Definitionen und Quellen.", "analysis", "45 min", ["Reports", "Dashboard Export"], ["IST-KPI-Inventar"], ["Alt-Report importiert"]),
      step("1.5.5", "Prozessanalyse", "Mappt Prozessketten, Bottlenecks und Datenflüsse.", "process-mining", "automatisch", ["Event Logs", "Interviews"], ["Process Map", "Bottlenecks"], ["Event Log vorhanden"]),
      step("1.5.6", "Gap-Analyse", "Vergleicht IST gegen Zielbild, Tabellenstakes und Audit-Anforderungen.", "analysis", "30 min", ["IST-KPIs", "Process Map"], ["Gap-Liste", "Risk Register"], ["Gap erkannt"]),
    ],
  },
  {
    id: "1.6",
    title: "KI-Enterprise Mining",
    subtitle: "Benchmark, KPI-Vorschlag, Zielwerte, Priorisierung, Kausalität und Simulation.",
    stream: "Analyse & Definition",
    objective: "Aus Daten und Zielsystemen steuerbare KPI- und Maßnahmenlogik erzeugen.",
    steps: [
      step("1.6.1", "Benchmark-Analyse", "Vergleicht relevante Kennzahlen mit externen und internen Benchmarks.", "benchmark", "automatisch", ["KPI-Inventar", "Benchmark Sources"], ["Benchmark Set"], ["Benchmark TTL abgelaufen"]),
      step("1.6.2", "KPI-Vorschlagsgenerierung", "Erzeugt KPI-Kandidaten mit Score, Begründung und Review-Status.", "kpi-generator", "automatisch", ["Ziele", "Datenprofile"], ["KPI-Kandidaten"], ["neues Ziel"]),
      step("1.6.3", "Zielwert-Berechnung", "Berechnet Zielwert-Spannen aus Historie, Benchmarks und Ambition.", "target-setting", "automatisch", ["KPI-Kandidaten", "Historie"], ["Target Ranges"], ["Baseline bereit"]),
      step("1.6.4", "Priorisierung", "Sortiert KPIs und Maßnahmen nach Impact, Confidence, Aufwand und Risiko.", "prioritization", "20 min", ["Kandidaten", "Risk Register"], ["Priorisierte Shortlist"], ["zu viele Kandidaten"]),
      step("1.6.5", "Kausalanalyse", "Prüft Ursache-Wirkung-Hypothesen und Driver-Tree-Pfade.", "causal-analysis", "automatisch", ["Shortlist", "Process Map"], ["RCA-Hypothesen", "Driver Paths"], ["Anomalie erkannt"]),
      step("1.6.6", "Szenario-Simulation", "Simuliert ROI, Cash, Risiko und Maßnahmenwirkung mit deterministischem Seed.", "simulation", "automatisch", ["Targets", "Treiber"], ["P10/P50/P90 Szenario"], ["Business Case angefragt"]),
    ],
  },
  {
    id: "1.7",
    title: "Ergebnisvalidierung",
    subtitle: "Aufbereitung, Workshop, Feedback-Integration und Iteration.",
    stream: "Analyse & Definition",
    objective: "Agentenergebnisse menschlich prüfen und auditierbar freigeben.",
    steps: [
      step("1.7.1", "Ergebnisaufbereitung", "Verdichtet Kandidaten, Benchmarks, RCA und Szenarien für C-Level Review.", "report", "automatisch", ["Shortlist", "Evidence"], ["Review Pack"], ["Review Termin"]),
      step("1.7.2", "Validierungsworkshop", "Führt Plausibilisierung, Owner-Zuordnung und Einwände zusammen.", "presentation", "60 min", ["Review Pack"], ["Workshop-Protokoll"], ["Workshop startet"]),
      step("1.7.3", "Feedback-Integration", "Übersetzt Feedback in KPI-Definitionen, Backlog und Risikoanpassungen.", "feedback", "automatisch", ["Workshop-Protokoll"], ["Feedback Summary", "Backlog"], ["Feedback eingegangen"]),
      step("1.7.4", "Iterationsschleife", "Startet gezielte zweite Analyse nur für strittige oder unsichere Punkte.", "analysis", "automatisch", ["Backlog", "Unsicherheiten"], ["Iteration Result"], ["Confidence niedrig"]),
    ],
  },
  {
    id: "1.8",
    title: "SOLL-Definition",
    subtitle: "Workshop-Vorbereitung, Abstimmung, Zielwerte und Messgrößen.",
    stream: "Analyse & Definition",
    objective: "Das Zielsystem als umsetzbaren Steuerungsvertrag formulieren.",
    steps: [
      step("1.8.1", "Workshop-Vorbereitung", "Erstellt Agenda, Entscheidungsfragen und Evidence-Dossier.", "planning", "30 min", ["Feedback Summary"], ["Workshop Agenda"], ["SOLL Workshop geplant"]),
      step("1.8.2", "Stakeholder-Abstimmung", "Klärt Zielkonflikte zwischen CEO/CFO/COO/Controller.", "communication", "45 min", ["Stakeholder Map", "Zielkonflikte"], ["Alignment Notes"], ["Zielkonflikt erkannt"]),
      step("1.8.3", "Zielwert-Finalisierung", "Fixiert Zielwerte, Toleranzen, Timeframes und Risikoband.", "target-setting", "30 min", ["Target Ranges"], ["Final Targets"], ["Ziel freigeben"]),
      step("1.8.4", "Messgrößen-Definition", "Definiert KPI-Formeln, Granularität, Owner und Datenklassifikation.", "kpi-definition", "automatisch", ["Final Targets", "KPI Shortlist"], ["Metric Contracts"], ["KPI approved"]),
    ],
  },
  {
    id: "1.9",
    title: "Zielsetzgestaltung",
    subtitle: "Erhebung, Verantwortlichkeiten, Reporting und Schwellenwerte.",
    stream: "Analyse & Definition",
    objective: "Messbarkeit, Owner und Eskalationslogik absichern.",
    steps: [
      step("1.9.1", "Erhebungsinstrumente", "Legt Quellen, Sync-Cadence und Messinstrumente für jede KPI fest.", "data-integration", "automatisch", ["Metric Contracts"], ["Measurement Plan"], ["Contract erstellt"]),
      step("1.9.2", "Verantwortlichkeiten", "Ordnet KPI Owner, Data Owner und Approver zu.", "planning", "20 min", ["Measurement Plan", "RACI"], ["Owner Matrix"], ["Owner leer"]),
      step("1.9.3", "Reporting-Design", "Entwirft Briefing-, Dashboard- und Audit-Sichten je Persona.", "dashboard", "automatisch", ["Owner Matrix", "Personas"], ["Reporting Layout"], ["Briefing Cadence gesetzt"]),
      step("1.9.4", "Schwellenwerte", "Definiert Warnungen, Eskalationen und HITL-Ausloeser.", "prioritization", "20 min", ["Final Targets"], ["Threshold Rules"], ["SLA/Limit gesetzt"]),
    ],
  },
  {
    id: "1.10",
    title: "Finale Zieldefinition",
    subtitle: "Dokumentation, Freigabe und Baseline.",
    stream: "Analyse & Definition",
    objective: "Zielsystem versionieren und für Betrieb einfrieren.",
    steps: [
      step("1.10.1", "Dokumentation", "Dokumentiert Zielsystem, KPI-Verträge, Owner und Evidence.", "document", "automatisch", ["Contracts", "Owner Matrix"], ["Zieldokumentation"], ["Freigabe naht"]),
      step("1.10.2", "Freigabe", "Führt HITL-Freigabe für KPI-Set, Targets und Reporting durch.", "feedback", "15 min", ["Zieldokumentation"], ["Approval Record"], ["Approver bereit"]),
      step("1.10.3", "Baseline-Erstellung", "Schreibt Baseline-Werte, Contract-Versionen und Trace-IDs fest.", "data", "automatisch", ["Approval Record", "Gold Mart"], ["Baseline Snapshot"], ["Freigabe erteilt"]),
    ],
  },
  {
    id: "1.11",
    title: "Abschlussbericht",
    subtitle: "Bericht, Präsentation und Handlungsempfehlungen.",
    stream: "Analyse & Definition",
    objective: "Entscheidungsfaehige Zusammenfassung mit Maßnahmenpaket erzeugen.",
    steps: [
      step("1.11.1", "Berichterstellung", "Generiert C-Level-Abschlussbericht mit Evidenzverweisen.", "report", "automatisch", ["Baseline Snapshot", "Evidence"], ["Abschlussbericht"], ["Baseline erstellt"]),
      step("1.11.2", "Präsentationserstellung", "Erstellt Vorstand-/Workshop-Folien aus Bericht und Storyline.", "presentation", "automatisch", ["Abschlussbericht"], ["Präsentation"], ["Präsentation angefragt"]),
      step("1.11.3", "Handlungsempfehlungen", "Formuliert priorisierte Maßnahmen mit Owner, Impact und Approval-Status.", "recommendation", "automatisch", ["RCA", "Targets"], ["Maßnahmenliste"], ["Zielabweichung"]),
    ],
  },
  {
    id: "1.12",
    title: "Kommunikation",
    subtitle: "Kommunikationskonzept, Zielgruppen, Rollout und Feedback.",
    stream: "Analyse & Definition",
    objective: "Akzeptanz, Verantwortlichkeit und Betriebsroutine vorbereiten.",
    steps: [
      step("1.12.1", "Kommunikationskonzept", "Plant Botschaften, Cadence, Kanäle und Freigaben.", "communication", "30 min", ["Maßnahmenliste"], ["Kommunikationskonzept"], ["Rollout geplant"]),
      step("1.12.2", "Zielgruppenspezifische Aufbereitung", "Passt Inhalte für Vorstand, Bereichsleitung, Owner und Controller an.", "content", "automatisch", ["Präsentation", "Audience Map"], ["Zielgruppenpakete"], ["Audience Segment gesetzt"]),
      step("1.12.3", "Rollout-Einsetzung", "Aktiviert Briefings, Dashboards, Owner-Routinen und Kommunikationskanäle.", "dashboard", "automatisch", ["Zielgruppenpakete", "Threshold Rules"], ["Live Cockpit", "Briefing Cadence"], ["Go-live"]),
      step("1.12.4", "Feedback-Sammlung", "Sammelt Akzeptanz, Einwände, neue Fragen und Coaching-Bedarf.", "feedback", "laufend", ["Live Cockpit", "Briefings"], ["Feedback Backlog"], ["Briefing gelesen"]),
    ],
  },
  {
    id: "2.0",
    title: "Handlungsfelder & Umsetzung",
    subtitle: "Operative Ziele, Maßnahmen, Change und Wirkungsmessung.",
    stream: "Umsetzung",
    objective: "Analyseergebnisse in nachvollziehbare Umsetzung überführen.",
    steps: [
      step("2.0.1", "Handlungsfeld-Clustering", "Gruppiert Maßnahmen nach Wirkung, Bereich und Abhängigkeit.", "prioritization", "30 min", ["Maßnahmenliste"], ["Handlungsfelder"], ["Maßnahmen approved"]),
      step("2.0.2", "Maßnahmenplanung", "Erstellt Umsetzungsplan mit Owner, Aufwand, Risiko und HITL-Status.", "recommendation", "45 min", ["Handlungsfelder"], ["Action Plan"], ["Handlungsfeld gewählt"]),
      step("2.0.3", "Change-Kommunikation", "Bereitet Kommunikationspakete für Rollout und Widerstände vor.", "communication", "automatisch", ["Action Plan"], ["Change Pack"], ["Rollout startet"]),
      step("2.0.4", "Umsetzungsmonitoring", "Verfolgt Fortschritt, KPI-Wirkung und Eskalationen in Echtzeit.", "dashboard", "laufend", ["Action Plan", "KPI Stream"], ["Impact Monitor"], ["Statuswechsel"]),
      step("2.0.5", "HITL-Aktionsfreigabe", "Führt externe Wirkung über Approval, n8n und Audit-Log aus.", "recommendation", "laufend", ["Impact Monitor", "Approver"], ["Approved Action Run"], ["Aktion fällig"]),
    ],
  },
  {
    id: "3.0",
    title: "Befaehigung & Nachhaltigkeit",
    subtitle: "Enablement, Zielerreichung, Verbesserung und Organisationsentwicklung.",
    stream: "Nachhaltigkeit",
    objective: "KPI-System in Routine, Lernen und kontinuierliche Verbesserung überführen.",
    steps: [
      step("3.0.1", "Owner-Coaching", "Trainiert KPI Owner in Interpretation, Eskalation und Review-Routine.", "coaching", "laufend", ["Owner Matrix", "Briefings"], ["Coaching Plan"], ["neuer Owner"]),
      step("3.0.2", "Zielerreichungs-Tracking", "Misst Fortschritt gegen Targets und Maßnahmenwirkung.", "dashboard", "laufend", ["KPI Stream", "Action Runs"], ["Goal Tracking"], ["Review-Cadence"]),
      step("3.0.3", "Kontinuierliche Verbesserung", "Schliesst Feedback, neue Daten und Lessons Learned in Contracts und Prozesse zurück.", "feedback", "laufend", ["Feedback Backlog", "Goal Tracking"], ["Improvement Backlog"], ["Review abgeschlossen"]),
      step("3.0.4", "Organisationsentwicklung", "Erkennt Reifegradfortschritt, Trainingsbedarf und skalierbare Best Practices.", "coaching", "monatlich", ["Adoption Metrics", "Improvement Backlog"], ["Maturity Update"], ["Adoption niedrig"]),
    ],
  },
];

export const CORE_PHASE_COUNT = 12;

export function allProcessSteps() {
  return PROCESS_PHASES.flatMap((phase) => phase.steps.map((step) => ({ ...step, phase })));
}

export function findAgent(agentId: string) {
  return PROCESS_AGENTS.find((agent) => agent.id === agentId);
}

function step(
  id: string,
  title: string,
  description: string,
  agentId: string,
  duration: string,
  inputs: string[],
  outputs: string[],
  triggers: string[],
): ProcessStep {
  return { id, title, description, agentId, duration, inputs, outputs, triggers };
}
